import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, HeartHandshake } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/data";
import { useScrollTo } from "@/hooks/useLenis";
import { Logo } from "@/components/Logo";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const scrollTo = useScrollTo();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled || open ? "bg-sand-50/85 shadow-[0_1px_0_rgba(19,64,116,0.08)] backdrop-blur-xl" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-12">
        <button onClick={() => go("#beranda")} className="flex items-center gap-3 text-left" data-testid="nav-logo">
          <Logo />
          <span className="leading-tight">
            <span className="block font-display text-xl text-navy">{SITE.name}</span>
            <span className="eyebrow block text-[9px] text-teal">{SITE.officialName} · Jembrana</span>
          </span>
        </button>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Navigasi utama">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="group relative rounded-full px-4 py-2 text-sm font-medium text-navy/80 transition-colors duration-300 hover:text-navy"
              data-testid={`nav-link-${l.href.slice(1)}`}
            >
              {l.label}
              <span className="absolute inset-x-4 -bottom-0.5 h-px origin-left scale-x-0 bg-gold transition-transform duration-300 group-hover:scale-x-100" />
            </button>
          ))}
          <button onClick={() => go("#donasi")} className="btn-accent ml-4 !px-5 !py-2.5" data-testid="nav-donate-button">
            <HeartHandshake className="h-4 w-4" /> Donasi
          </button>
        </nav>

        <button
          className="rounded-full border border-navy/15 p-2.5 text-navy lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Buka menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-navy/10 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="rounded-2xl px-4 py-3 text-left font-display text-2xl text-navy transition-colors hover:bg-navy/5"
                  data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                >
                  {l.label}
                </button>
              ))}
              <button onClick={() => go("#donasi")} className="btn-accent mt-3" data-testid="nav-mobile-donate-button">
                <HeartHandshake className="h-4 w-4" /> Donasi & Infaq
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
